import React, { useEffect, useMemo, useRef } from 'react';
import { Animated, Easing, Image, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import Icon from 'react-native-vector-icons/Feather';
import { Colors, Radius, Shadow, Typography } from '../../theme';
import type { ChatMessage, ChatReaction } from '../../types';
import { formatRelativeTime } from '../../utils/chatFormatting';
import ChatAvatar from './ChatAvatar';
import ReplyPreview from './ReplyPreview';
import SharedEventCard from './SharedEventCard';
import GifMessageBubble from './GifMessageBubble';
import VoiceMessageBubble from './VoiceMessageBubble';
import { useTheme } from '../../theme/useTheme';
import type { TonyteTheme } from '../../theme/themes';
import { daylightStyle } from '../../theme/daylightStyle';
import { useUserProfile } from '../../utils/userProfiles';

interface Props {
  item: ChatMessage;
  isMe: boolean;
  translatedText?: string;
  t: (key: string, fallback?: string) => string;
  showAvatarAndName: boolean;
  preferSnapshotIdentity?: boolean;
  reactions: ChatReaction[];
  seenLabel?: string;
  onOpenSharedEvent: (eventId: string) => void;
  onOpenSenderProfile?: (userId: string) => void;
  onOpenSharedVenue?: (venueId: string) => void;
  onOpenSharedLocation?: (location: NonNullable<ChatMessage['sharedVenue']>) => void;
  // Whether THIS message's shared event is known to be expired/unavailable —
  // computed by the caller (cheap snapshot check + any live-confirmed
  // result), not derived here, since the live event doc is the real source
  // of truth and this component has no Firestore access.
  eventExpired?: boolean;
  onOpenGif?: (url: string) => void;
  onRetryVoiceUpload?: (item: ChatMessage) => void;
  onCancelVoiceUpload?: (item: ChatMessage) => void;
  onLongPress: (item: ChatMessage) => void;
}

function groupReactions(reactions: ChatReaction[]): Array<{ emoji: string; count: number }> {
  const totals = new Map<string, number>();
  reactions.forEach(r => totals.set(r.emoji, (totals.get(r.emoji) || 0) + 1));
  return Array.from(totals.entries()).map(([emoji, count]) => ({ emoji, count }));
}

const ChatMessageBubble = React.memo(function ChatMessageBubble({
  item,
  isMe,
  translatedText,
  t,
  showAvatarAndName,
  preferSnapshotIdentity = false,
  reactions,
  seenLabel,
  onOpenSharedEvent,
  onOpenSenderProfile,
  onOpenSharedVenue,
  onOpenSharedLocation,
  eventExpired,
  onOpenGif,
  onRetryVoiceUpload,
  onCancelVoiceUpload,
  onLongPress,
}: Props) {
  const { theme } = useTheme();
  const appearance = useMemo(() => createAppearanceStyles(theme), [theme]);
  const fade = useRef(new Animated.Value(0)).current;
  const slide = useRef(new Animated.Value(1)).current;
  const scale = useRef(new Animated.Value(0.96)).current;
  // senderPhotoURL is a snapshot from when the message was sent; show the
  // sender's current profile picture and fall back to the snapshot.
  const senderProfile = useUserProfile(!preferSnapshotIdentity && !isMe && showAvatarAndName ? item.senderId : null);
  const senderPhotoUri = preferSnapshotIdentity ? item.senderPhotoURL : senderProfile?.profileImage || item.senderPhotoURL;

  useEffect(() => {
    // Runs once on mount only (React.memo + a stable key per message id means
    // an existing bubble never remounts on later re-renders) — new messages
    // animate in, the rest of the list is untouched.
    Animated.parallel([
      Animated.timing(fade, { toValue: 1, duration: isMe ? 200 : 260, easing: Easing.out(Easing.cubic), useNativeDriver: true }),
      Animated.timing(slide, { toValue: 0, duration: isMe ? 200 : 260, easing: Easing.out(Easing.cubic), useNativeDriver: true }),
      Animated.spring(scale, { toValue: 1, useNativeDriver: true, speed: 18, bounciness: 6 }),
    ]).start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenSharedEvent = () => {
    if (eventExpired) return;
    if (item.sharedEvent?.eventId) onOpenSharedEvent(item.sharedEvent.eventId);
  };
  const handleOpenSharedVenue = () => {
    if (item.sharedVenue?.venueId) {
      onOpenSharedVenue?.(item.sharedVenue.venueId);
    } else if (item.sharedVenue) {
      onOpenSharedLocation?.(item.sharedVenue);
    }
  };
  const handleOpenGif = () => {
    if (item.gif?.url) onOpenGif?.(item.gif.url);
  };
  const handleLongPress = () => {
    if (item.status === 'uploading' || item.status === 'failed') return;
    onLongPress(item);
  };

  const reactionGroups = useMemo(() => groupReactions(reactions), [reactions]);
  const animatedStyle = {
    opacity: fade,
    transform: [
      { translateY: slide.interpolate({ inputRange: [0, 1], outputRange: [0, 8] }) },
      { scale },
    ],
  };

  if (item.type === 'vibe_icebreaker') {
    return (
      <Animated.View style={[s.systemPromptWrap, animatedStyle]}>
        <View style={[s.systemPromptCard, appearance.systemPromptCard]}>
          <LinearGradient
            pointerEvents="none"
            colors={theme.isDark
              ? ['rgba(161,140,209,0.16)', 'rgba(161,140,209,0.03)']
              : ['rgba(161,140,209,0.14)', 'rgba(255,252,246,0.72)']}
            style={StyleSheet.absoluteFill}
          />
          <View style={s.systemPromptHeader}>
            <Icon name="zap" size={13} color="#C084FC" />
            <Text style={[s.systemPromptTitle, appearance.systemPromptTitle]}>{item.text || t('chat.vibePromptsReady')}</Text>
          </View>
          {(item.prompts || []).map((prompt, index) => (
            <Text key={`${item.id}-prompt-${index}`} style={[s.systemPromptText, appearance.systemPromptText]}>
              {index + 1}. {prompt}
            </Text>
          ))}
          <Text style={[s.systemPromptTime, appearance.systemPromptTime]}>{formatRelativeTime(item.createdAt)}</Text>
        </View>
      </Animated.View>
    );
  }

  const replyQuote = item.replyTo ? (
    <ReplyPreview senderName={item.replyTo.senderName} text={item.replyTo.text} tint={isMe ? 'me' : 'them'} />
  ) : null;

  return (
    <Animated.View style={[s.msgRow, isMe ? s.msgRowMe : s.msgRowThem, animatedStyle]}>
      {!isMe && showAvatarAndName ? (
        <TouchableOpacity
          onPress={() => onOpenSenderProfile?.(item.senderId)}
          disabled={!onOpenSenderProfile}
          accessibilityRole={onOpenSenderProfile ? 'button' : undefined}
        >
          <ChatAvatar uri={senderPhotoUri} size={30} />
        </TouchableOpacity>
      ) : !isMe ? (
        <View style={s.avatarSpacer} />
      ) : null}
      <View style={[s.msgOuter, isMe ? s.msgOuterMe : s.msgOuterThem]}>
        {showAvatarAndName ? (
          <TouchableOpacity
            disabled={isMe || !onOpenSenderProfile}
            onPress={() => onOpenSenderProfile?.(item.senderId)}
          >
            <Text style={[s.msgSenderName, isMe ? s.msgSenderMe : s.msgSenderThem, appearance.senderName]}>
              {isMe ? t('chat.you') : item.senderName || t('chat.traveler')}
            </Text>
          </TouchableOpacity>
        ) : null}

        <LinearGradient
          pointerEvents="none"
          colors={isMe
            ? ['rgba(242,125,38,0)', 'rgba(242,125,38,0.038)', 'rgba(242,125,38,0)']
            : ['rgba(210,222,240,0)', 'rgba(210,222,240,0.026)', 'rgba(210,222,240,0)']}
          start={{ x: isMe ? 1 : 0, y: 0.5 }}
          end={{ x: isMe ? 0 : 1, y: 0.5 }}
          style={[s.bubbleAura, isMe ? s.bubbleAuraMe : s.bubbleAuraThem]}
        />
        <TouchableOpacity
          activeOpacity={0.92}
          onLongPress={handleLongPress}
          delayLongPress={280}
          style={[s.msgBubble, isMe ? s.msgBubbleMe : s.msgBubbleThem, isMe ? appearance.msgBubbleMe : appearance.msgBubbleThem]}
        >
          {isMe ? (
            <LinearGradient
              pointerEvents="none"
              colors={theme.isDark ? ['#FF9D5C', Colors.sunset, '#D9640F'] : ['#FFF3E5', '#F6DFCA', '#EBC9A9']}
              start={{ x: 0.1, y: 0 }}
              end={{ x: 0.95, y: 1 }}
              style={StyleSheet.absoluteFill}
            />
          ) : (
            <LinearGradient
              pointerEvents="none"
              colors={theme.isDark ? ['rgba(255,255,255,0.1)', 'rgba(255,255,255,0.02)'] : theme.gradients.surfaceElevated}
              start={{ x: 0.1, y: 0 }}
              end={{ x: 0.9, y: 1 }}
              style={StyleSheet.absoluteFill}
            />
          )}
          {isMe ? (
            <View pointerEvents="none" style={s.shineStripe} />
          ) : null}

          {replyQuote}

          {item.type === 'image' && item.imageUrl ? (
            <Image source={{ uri: item.imageUrl }} style={s.imageMessage} resizeMode="cover" resizeMethod="resize" />
          ) : null}

          {item.type === 'gif' && item.gif ? (
            <GifMessageBubble gif={item.gif} onPress={handleOpenGif} />
          ) : null}

          {item.type === 'voice' ? (
            <VoiceMessageBubble
              audioUrl={item.audioUrl}
              audioMimeType={item.audioMimeType}
              duration={item.duration || 0}
              waveformData={item.waveformData}
              status={item.status || 'sent'}
              uploadProgress={item.uploadProgress}
              isMe={isMe}
              onRetry={onRetryVoiceUpload ? () => onRetryVoiceUpload(item) : undefined}
              onCancelUpload={onCancelVoiceUpload ? () => onCancelVoiceUpload(item) : undefined}
            />
          ) : null}

          {item.type === 'shared_event' && item.sharedEvent ? (
            <SharedEventCard
              variant="event"
              label={eventExpired ? t('chat.eventExpiredLabel') : t('chat.tonyteEvent')}
              title={item.sharedEvent.title}
              metaLines={[item.sharedEvent.time, item.sharedEvent.location]}
              teaser={item.sharedEvent.teaser}
              imageUrl={item.sharedEvent.imageUrl}
              ctaLabel={eventExpired ? t('chat.eventExpired') : t('chat.openEvent')}
              onPress={handleOpenSharedEvent}
              isMe={isMe}
              expired={eventExpired}
            />
          ) : null}

          {item.type === 'shared_venue' && item.sharedVenue ? (
            item.sharedVenue.venueId ? (
              <SharedEventCard
                variant="venue"
                label={t('chat.tonyteVenue')}
                title={item.sharedVenue.name}
                metaLines={[item.sharedVenue.category, item.sharedVenue.city || '']}
                imageUrl={item.sharedVenue.coverUrl}
                ctaLabel={t('event.openVenue')}
                onPress={handleOpenSharedVenue}
                isMe={isMe}
              />
            ) : (
              <SharedEventCard
                variant="location"
                label={t('chat.sharedLocationLabel')}
                title={item.sharedVenue.address || t('chat.sharedLocationLabel')}
                metaLines={[]}
                ctaLabel={t('chat.viewOnMap')}
                onPress={handleOpenSharedVenue}
                isMe={isMe}
              />
            )
          ) : null}

          {item.text && item.type !== 'shared_event' && item.type !== 'shared_venue' && item.type !== 'gif' && item.type !== 'voice' ? (
            <Text style={[s.msgText, appearance.msgText]}>{translatedText || item.text}</Text>
          ) : null}

          <View style={s.metaRow}>
            <Text style={[s.msgTimestamp, appearance.msgTimestamp]}>{formatRelativeTime(item.createdAt)}</Text>
            {isMe && seenLabel ? (
              <View style={s.receiptRow}>
                <Icon name={seenLabel.startsWith('Seen') ? 'check-circle' : 'check'} size={10} color={theme.isDark ? 'rgba(255,255,255,0.85)' : theme.colors.iconSubtle} />
                <Text style={[s.receiptText, appearance.receiptText]}>{seenLabel}</Text>
              </View>
            ) : null}
          </View>
        </TouchableOpacity>

        {reactionGroups.length > 0 ? (
          <View style={[s.reactionsRow, isMe ? s.reactionsRowMe : s.reactionsRowThem]}>
            {reactionGroups.map(group => (
              <View key={group.emoji} style={[s.reactionPill, appearance.reactionPill]}>
                <Text style={s.reactionEmoji}>{group.emoji}</Text>
                {group.count > 1 ? <Text style={[s.reactionCount, appearance.reactionCount]}>{group.count}</Text> : null}
              </View>
            ))}
          </View>
        ) : null}
      </View>
    </Animated.View>
  );
});

export default ChatMessageBubble;

const s = StyleSheet.create({
  msgRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 8,
    marginBottom: 2,
  },
  msgRowMe: { justifyContent: 'flex-end' },
  msgRowThem: { justifyContent: 'flex-start' },
  avatarSpacer: { width: 30 },
  msgOuter: { maxWidth: '78%', position: 'relative' },
  msgOuterMe: { alignItems: 'flex-end' },
  msgOuterThem: { alignItems: 'flex-start' },
  msgSenderName: {
    fontSize: 11,
    fontWeight: Typography.bold,
    marginBottom: 3,
    marginHorizontal: 4,
  },
  msgSenderMe: { color: 'rgba(255,255,255,0.55)' },
  msgSenderThem: { color: 'rgba(255,255,255,0.48)' },
  msgBubble: {
    borderRadius: Radius.xxl,
    paddingHorizontal: 16,
    paddingVertical: 11,
    overflow: 'hidden',
    borderWidth: 1,
  },
  msgBubbleMe: {
    borderColor: 'rgba(255,255,255,0.24)',
    ...Shadow.glow,
    shadowOpacity: 0.16,
    shadowRadius: 18,
    elevation: 8,
  },
  msgBubbleThem: {
    borderColor: 'rgba(255,255,255,0.13)',
    ...Shadow.card,
    shadowOpacity: 0.12,
    shadowRadius: 12,
    elevation: 5,
  },
  bubbleAura: {
    position: 'absolute',
    top: -20,
    bottom: -20,
    borderRadius: 48,
  },
  bubbleAuraMe: {
    left: -28,
    right: -14,
  },
  bubbleAuraThem: {
    left: -16,
    right: -26,
  },
  shineStripe: {
    position: 'absolute',
    top: -30,
    left: -20,
    width: 40,
    height: 160,
    backgroundColor: 'rgba(255,255,255,0.12)',
    transform: [{ rotate: '20deg' }],
  },
  imageMessage: {
    width: 200,
    height: 150,
    borderRadius: Radius.md,
    marginBottom: 4,
    backgroundColor: Colors.bgCardAlt,
  },
  msgText: {
    color: '#fff',
    fontSize: Typography.sm,
    lineHeight: 20,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 5,
  },
  msgTimestamp: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 10,
  },
  receiptRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
  },
  receiptText: {
    color: 'rgba(255,255,255,0.75)',
    fontSize: 9.5,
    fontWeight: Typography.medium,
  },
  reactionsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: 4,
  },
  reactionsRowMe: { justifyContent: 'flex-end' },
  reactionsRowThem: { justifyContent: 'flex-start' },
  reactionPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: Radius.pill,
    backgroundColor: 'rgba(10,14,22,0.65)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.14)',
  },
  reactionEmoji: { fontSize: 12 },
  reactionCount: { color: 'rgba(255,255,255,0.75)', fontSize: 10, fontWeight: Typography.bold },
  systemPromptWrap: { alignItems: 'center', marginVertical: 6 },
  systemPromptCard: {
    maxWidth: '86%',
    borderRadius: Radius.lg,
    padding: 14,
    gap: 6,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: 'rgba(192,132,252,0.3)',
    backgroundColor: 'rgba(192,132,252,0.08)',
  },
  systemPromptHeader: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  systemPromptTitle: { color: '#E9D5FF', fontSize: Typography.sm, fontWeight: Typography.bold, flex: 1 },
  systemPromptText: { color: 'rgba(255,255,255,0.85)', fontSize: Typography.xs, lineHeight: 18 },
  systemPromptTime: { color: 'rgba(255,255,255,0.4)', fontSize: 10, marginTop: 2 },
});

const createAppearanceStyles = (theme: TonyteTheme) => StyleSheet.create({
  senderName: daylightStyle(theme, { color: theme.colors.textSecondary }),
  msgBubbleMe: daylightStyle(theme, {
    borderColor: 'rgba(190,120,62,0.25)',
    ...theme.shadows.elevated,
  }),
  msgBubbleThem: daylightStyle(theme, {
    borderColor: theme.colors.cardBorder,
    ...theme.shadows.card,
  }),
  msgText: daylightStyle(theme, { color: theme.colors.textPrimary }),
  msgTimestamp: daylightStyle(theme, { color: theme.colors.textSecondary }),
  receiptText: daylightStyle(theme, { color: theme.colors.textSecondary }),
  reactionPill: daylightStyle(theme, {
    backgroundColor: theme.colors.surfaceElevated,
    borderColor: theme.colors.border,
  }),
  reactionCount: daylightStyle(theme, { color: theme.colors.textSecondary }),
  systemPromptCard: daylightStyle(theme, {
    backgroundColor: theme.colors.surfaceElevated,
    borderColor: 'rgba(139,92,246,0.20)',
    ...theme.shadows.card,
  }),
  systemPromptTitle: daylightStyle(theme, { color: '#70539C' }),
  systemPromptText: daylightStyle(theme, { color: theme.colors.textPrimary }),
  systemPromptTime: daylightStyle(theme, { color: theme.colors.textMuted }),
});
